import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Video, Clock, User as UserIcon, Circle, PhoneOff, Star, Send } from 'lucide-react';
import { useAuth, api } from '../context/AuthContext';
import { browserTz, tzLabel, formatInTz } from '../lib/tz';
import { DailyCall } from '../components/DailyCall';
import './Lobby.css';

const fmtSecs = (secs) => {
  if (secs == null || isNaN(secs)) return '--:--';
  const s = Math.max(0, secs);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`;
  return `${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`;
};

const Lobby = () => {
  const { bookingId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState(null);           // /lobby-state response
  const [ticks, setTicks] = useState(0);              // triggers re-render every 1s
  const [callRoom, setCallRoom] = useState(null);     // { room_url, token }
  const [joining, setJoining] = useState(false);
  const [showRating, setShowRating] = useState(false); // post-call student rating
  const [ratingValue, setRatingValue] = useState(5);
  const [reviewText, setReviewText] = useState('');
  const [submittingRating, setSubmittingRating] = useState(false);
  const autoEndedRef = useRef(false);
  const resolveTriedRef = useRef(false);
  // Late-mentor modal: shown once per booking to a student when scheduled
  // time passes and the mentor isn't in the room yet. Auto-join into the
  // Daily room is PAUSED until the student taps "Got it".
  const [showLateMentor, setShowLateMentor] = useState(false);
  const [lateAcked, setLateAcked] = useState(() => {
    try { return sessionStorage.getItem(`late-ack:${bookingId}`) === '1'; }
    catch { return false; }
  });
  // Teacher pre-session heads-up: shown once per booking the moment a teacher
  // opens the lobby for a session that hasn't started yet. Mirrors the
  // student-side late-mentor modal.
  const [showTeacherEarly, setShowTeacherEarly] = useState(false);
  const [teacherEarlyAcked, setTeacherEarlyAcked] = useState(() => {
    try { return sessionStorage.getItem(`teacher-early-ack:${bookingId}`) === '1'; }
    catch { return false; }
  });
  const timerRef = useRef(null);
  const [confirmLeave, setConfirmLeave] = useState(false);
  const leavingRef = useRef(false);

  // ---- Fetch lobby-state every 5s ----
  useEffect(() => {
    if (!user || !bookingId) return;
    let cancelled = false;
    const fetchOnce = async () => {
      try {
        const s = await api(`/api/bookings/${bookingId}/lobby-state`);
        if (!cancelled) setState(s);
      } catch (e) { if (!cancelled) toast.error('Could not load session', { description: e.message }); }
    };
    fetchOnce();
    const iv = setInterval(fetchOnce, 5000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [bookingId, user]);

  // ---- Local 1-second tick so the countdown updates smoothly between polls ----
  useEffect(() => {
    timerRef.current = setInterval(() => setTicks(t => t + 1), 1000);
    return () => clearInterval(timerRef.current);
  }, []);

  // Compute live-adjusted seconds using server delta + local clock drift
  const now = Date.now();
  const serverAnchor = state ? new Date(state.server_time).getTime() : now;
  const drift = Math.floor((now - serverAnchor) / 1000);
  const secUntilOpen  = state ? Math.max(0, (state.seconds_until_open  ?? 0) - drift) : null;
  const secUntilStart = state ? (state.seconds_until_start ?? 0) - drift : null;
  const secUntilEnd   = state ? (state.seconds_until_end   ?? 0) - drift : null;

  const lobbyOpen = state && (state.lobby_open || secUntilOpen === 0);
  const started = secUntilStart !== null && secUntilStart <= 0;
  const ended = secUntilEnd !== null && secUntilEnd <= 0;

  const booking = state?.booking;
  const role = state?.role;
  const other = state?.counterparty || {};

  const teacherJoined = !!booking?.teacher_joined_at;
  const studentJoined = !!booking?.student_joined_at;

  // Grace-period expiry: 2 minutes after scheduled start time.
  // If the mentor still hasn't joined by then, the booking is effectively
  // dead — refund flow kicks in and neither party should be able to join.
  const graceExpired =
    secUntilStart !== null && secUntilStart <= -120 && !teacherJoined;
  // Treat grace-expired as a terminal state locally so the UI (and the
  // auto-resolve ping below) stop offering a Join button.
  const terminalStatuses = [
    'completed', 'no_show_refunded', 'no_show_pending_refund',
    'no_show_student_paid', 'cancelled_refunded', 'cancelled_no_refund',
  ];
  const statusTerminal = booking && terminalStatuses.includes(booking.status);
  const sessionDead = ended || graceExpired || statusTerminal;

  // Once ended OR grace-period expired without the mentor joining, ping
  // resolve-no-show once so the backend applies the terminal state (refund
  // or paid). We no longer wait for the full session window to close.
  useEffect(() => {
    if (!sessionDead || !booking || resolveTriedRef.current) return;
    if (terminalStatuses.includes(booking.status)) return;
    resolveTriedRef.current = true;
    // Human labels for backend status strings — the raw snake_case leaks
    // through to toasts and the no-show case was reading as "no show student
    // paid", confusing people into thinking the student paid someone else.
    const prettyStatus = (s) => {
      if (!s) return '';
      if (s === 'no_show_student_paid') return 'student no-show — you were paid';
      if (s === 'no_show_refunded') return 'mentor no-show — refunded';
      if (s === 'no_show_pending_refund') return 'mentor no-show — refund pending';
      if (s === 'cancelled_refunded') return 'cancelled — refunded';
      return s.replaceAll('_', ' ');
    };
    api(`/api/bookings/${bookingId}/resolve-no-show`, { method: 'POST' })
      .then(r => {
        if (r.status && r.status !== booking.status) {
          toast(r.status === 'completed' ? 'Session ended' : 'Session outcome recorded', {
            description: prettyStatus(r.status),
          });
        }
      })
      .catch(() => {});
  }, [sessionDead, booking, bookingId]);

  const canJoin = () => {
    if (!lobbyOpen) return false;
    if (sessionDead) return false;
    if (role === 'creator') return true;             // teacher joins whenever lobby is open
    if (role === 'user') return started;             // student can ONLY join at scheduled_time (auto-pushed)
    return false;
  };

  // ---- Auto-push student into call the moment scheduled_time is reached ----
  // Paused while the "mentor is late" modal is visible so the student can read
  // the grace-period notice before entering the empty room.
  const autoJoinedRef = useRef(false);
  useEffect(() => {
    if (autoJoinedRef.current) return;
    if (role !== 'user') return;
    if (!started || sessionDead) return;
    if (callRoom) return;                            // already in call
    // If the mentor is still a no-show at start time, surface the modal first
    // (once per booking) and hold the auto-join until the student acknowledges.
    if (!teacherJoined && !lateAcked) {
      if (!showLateMentor) setShowLateMentor(true);
      return;
    }
    autoJoinedRef.current = true;
    joinCall();
  }, [started, sessionDead, role, callRoom, teacherJoined, lateAcked, showLateMentor]);

  const dismissLateMentor = () => {
    try { sessionStorage.setItem(`late-ack:${bookingId}`, '1'); } catch { /* ignore */ }
    setLateAcked(true);
    setShowLateMentor(false);
  };

  // If the grace period lapses while the late-mentor modal is still open,
  // auto-close it — the student can no longer join and the modal copy
  // ("we'll give them 2 more minutes") is now stale.
  useEffect(() => {
    if (showLateMentor && graceExpired) setShowLateMentor(false);
  }, [showLateMentor, graceExpired]);

  // Teacher: the moment they open the lobby for an upcoming (or just-starting)
  // session, show the "you're early for A/V check" bubble — once per booking.
  useEffect(() => {
    if (role !== 'creator') return;
    if (teacherEarlyAcked) return;
    if (sessionDead) return;
    if (!booking) return;
    if (showTeacherEarly) return;
    setShowTeacherEarly(true);
  }, [role, teacherEarlyAcked, sessionDead, booking, showTeacherEarly]);

  const dismissTeacherEarly = () => {
    try { sessionStorage.setItem(`teacher-early-ack:${bookingId}`, '1'); }
    catch { /* ignore */ }
    setTeacherEarlyAcked(true);
    setShowTeacherEarly(false);
  };

  const joinCall = async () => {
    if (!canJoin() || joining) return;
    setJoining(true);
    try {
      await api(`/api/calls/create-room?booking_id=${bookingId}`, { method: 'POST' }).catch(() => {});
      const jd = await api(`/api/calls/${bookingId}/join`, { method: 'POST' });
      // Record join for no-show tracking
      api(`/api/bookings/${bookingId}/join`, { method: 'POST' }).catch(() => {});
      setCallRoom({ room_url: jd.room_url, token: jd.token });
    } catch (e) {
      toast.error('Could not join', { description: e.message });
    }
    setJoining(false);
  };

  const leaveCall = async (opts = {}) => {
    if (leavingRef.current) return;
    leavingRef.current = true;
    const skipRating = opts.skipRating;
    try { await api(`/api/calls/${bookingId}/end`, { method: 'POST' }); } catch { /* ignore */ }
    setCallRoom(null);
    setState(s => (s?.booking ? { ...s, booking: { ...s.booking, status: 'completed' } } : s));
    leavingRef.current = false;
    // Students rate the mentor on exit — but only if the mentor actually
    // showed up. No-show rounds end on a refund flow, not on a review.
    if (!skipRating && role === 'user' && booking && teacherJoined) {
      setShowRating(true);
    } else {
      navigate('/notifications');
    }
  };

  // Auto-end the call the moment the scheduled window closes OR the grace
  // period lapses with the mentor missing. Guards with a ref so we only fire
  // once even though `sessionDead` oscillates while state polls.
  useEffect(() => {
    if (!sessionDead || !callRoom || autoEndedRef.current) return;
    autoEndedRef.current = true;
    const msg = graceExpired && !ended
      ? "Mentor didn't arrive — ending call"
      : "Session time's up — ending call";
    toast(msg, { description: 'Thanks for using 6th Mate.' });
    leaveCall();
  }, [sessionDead, callRoom, graceExpired, ended]);

  const submitRating = async () => {
    if (!booking) return;
    setSubmittingRating(true);
    try {
      const creatorId = booking.creator_id === user.id ? booking.user_id : booking.creator_id;
      await api(`/api/calls/${bookingId}/rate`, {
        method: 'POST',
        body: JSON.stringify({
          booking_id: bookingId,
          rater_id: user.id,
          creator_id: creatorId,
          teachability_rating: ratingValue,
          skill_ratings: {},
          review_text: reviewText.trim(),
        }),
      });
      toast.success('Thanks for your review!');
      setShowRating(false);
      navigate('/notifications');
    } catch (err) {
      toast.error('Rating failed', { description: err.message || 'Please try again.' });
    }
    setSubmittingRating(false);
  };

  const skipRating = () => {
    setShowRating(false);
    navigate('/notifications');
  };

  if (!state) return (
    <div className="lobby-page" data-testid="lobby-loading">
      <div className="lobby-loading">Loading session…</div>
    </div>
  );

  // ---- In-call view ----
  if (callRoom) {
    return (
      <div className="lobby-call" data-testid="lobby-in-call">
        <div className="lobby-call-bar">
          <div className="lobby-call-title">
            <Circle size={10} fill="#ef4444" color="#ef4444" /> Live session
            {secUntilEnd !== null && !ended && (
              <span className="lobby-time-remaining">{fmtSecs(secUntilEnd)} left</span>
            )}
          </div>
          <button className="lobby-leave-btn" onClick={() => setConfirmLeave(true)} data-testid="lobby-leave-btn">
            <PhoneOff size={16} /> Leave
          </button>
        </div>
        <DailyCall roomUrl={callRoom.room_url} token={callRoom.token} onLeft={() => leaveCall()} />
        {confirmLeave && (
          <div className="lobby-confirm-overlay" data-testid="leave-confirm-modal">
            <div className="lobby-confirm-card">
              <h2>Leave this session?</h2>
              <p data-testid="leave-confirm-text">
                {role === 'creator'
                  ? 'Your student is expecting the full session. Ending early may lead to a poor review — and the call will end for both of you.'
                  : "Leaving now ends the call for both of you, and you'll lose the rest of your session time."}
              </p>
              <div className="lobby-confirm-actions">
                <button className="lobby-confirm-stay" onClick={() => setConfirmLeave(false)} data-testid="leave-confirm-stay">Stay in session</button>
                <button className="lobby-confirm-leave" onClick={() => { setConfirmLeave(false); leaveCall(); }} data-testid="leave-confirm-leave">Leave session</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ---- Lobby / waiting view ----
  const primaryLabel = (() => {
    if (graceExpired && !ended) {
      return role === 'creator'
        ? 'Session expired — grace period passed'
        : 'Your mentor didn\u2019t join in time — refund is being processed';
    }
    if (ended && !teacherJoined) return 'Teacher didn\u2019t show up';
    if (ended && !studentJoined && role === 'creator') return 'Student didn\u2019t show up — you were paid';
    if (ended) return 'Session ended';
    if (!lobbyOpen) return 'Lobby opens 2 minutes before your session';
    if (role === 'user' && !started) return 'Get ready — you\u2019ll join automatically at start time';
    if (role === 'user' && started && !teacherJoined) return 'Your mentor is late — refund is being processed';
    if (role === 'creator' && !teacherJoined) return 'Ready to join — set up your camera & audio';
    if (role === 'creator' && teacherJoined && !studentJoined && !started) return 'Waiting — your student joins at start time';
    if (role === 'creator' && teacherJoined && !studentJoined && started) return 'Waiting for your student';
    if (teacherJoined && studentJoined) return 'Both parties ready — tap Join';
    return '';
  })();

  return (
    <div className="lobby-page" data-testid="lobby-page">
      <div className="lobby-topbar">
        <button className="lobby-back" onClick={() => navigate(-1)} data-testid="lobby-back">
          <ArrowLeft size={18} />
        </button>
        <div className="lobby-topbar-title">
          <p className="lobby-with">Session with</p>
          <p className="lobby-name">{other.full_name || 'your session partner'}</p>
        </div>
        <span className="lobby-icon-placeholder" />
      </div>

      <section className="lobby-hero">
        <div className="lobby-avatar-wrap">
          {other.avatar_url
            ? <img src={other.avatar_url} alt="" className="lobby-avatar" />
            : <div className="lobby-avatar lobby-avatar-placeholder">
                <UserIcon size={40} />
              </div>}
        </div>

        <p className="lobby-status" data-testid="lobby-status">{primaryLabel}</p>

        {/* Countdown */}
        <div className="lobby-countdown-card" data-testid="lobby-countdown">
          {!lobbyOpen ? (
            <>
              {/* Two-column: "Lobby opens in MM:SS"  ·  "Session at HH:MM"
                  — makes it unambiguous that the lobby opens 2 min before the
                  session actually starts, which otherwise reads like one and
                  the same time. */}
              <div className="lobby-count-split" data-testid="lobby-count-split">
                <div className="lobby-count-col">
                  <span className="lobby-count-label">Lobby opens in</span>
                  <span className="lobby-count-value" data-testid="lobby-opens-in">{fmtSecs(secUntilOpen)}</span>
                </div>
                <div className="lobby-count-divider" />
                <div className="lobby-count-col">
                  <span className="lobby-count-label">Session at</span>
                  <span className="lobby-count-value lobby-count-value-time" data-testid="lobby-session-at">
                    {booking?.scheduled_date
                      ? formatInTz(booking.scheduled_date, user?.timezone || browserTz(), { weekday: undefined, day: undefined, month: undefined, hour: 'numeric', minute: '2-digit', hour12: false })
                      : '—'}
                  </span>
                  <span className="lobby-count-sub">
                    {booking?.scheduled_date
                      ? formatInTz(booking.scheduled_date, user?.timezone || browserTz(), { hour: undefined, minute: undefined, hour12: undefined, year: undefined })
                      : ''}
                  </span>
                  {booking?.scheduled_date && (() => {
                    const myTz = user?.timezone || browserTz();
                    const otherTz = other?.timezone || '';
                    const otherLabel = tzLabel(otherTz, other?.location);
                    if (!otherTz || otherTz === myTz) return null;
                    return (
                      <span className="lobby-count-sub" data-testid="lobby-session-other-tz" style={{ color: '#E07A5F', marginTop: 4 }}>
                        {formatInTz(booking.scheduled_date, otherTz, { hour12: false, year: undefined })} · {role === 'creator' ? 'student' : 'mentor'}{otherLabel ? ` (${otherLabel})` : ''}
                      </span>
                    );
                  })()}
                </div>
              </div>
              {role === 'creator' && (
                <p className="lobby-count-hint" data-testid="lobby-count-hint">
                  You'll enter the lobby 2 minutes early to set up camera and audio.
                </p>
              )}
            </>
          ) : ended ? (
            <>
              <span className="lobby-count-label">Session window closed</span>
              <span className="lobby-count-value done">{fmtSecs(0)}</span>
            </>
          ) : graceExpired ? (
            <>
              <span className="lobby-count-label">Grace period passed</span>
              <span className="lobby-count-value done" data-testid="lobby-grace-expired">Expired</span>
              <span className="lobby-count-sub">
                {role === 'user' ? 'Refund in progress' : 'This session has been closed'}
              </span>
            </>
          ) : started ? (
            <>
              <span className="lobby-count-label">Time remaining</span>
              <span className={`lobby-count-value ${secUntilEnd < 60 ? 'warning' : ''}`}>{fmtSecs(secUntilEnd)}</span>
              <span className="lobby-count-sub">of {booking.minutes} min session</span>
            </>
          ) : (
            <>
              <span className="lobby-count-label">Session starts in</span>
              <span className="lobby-count-value">{fmtSecs(secUntilStart)}</span>
              <span className="lobby-count-sub">Lobby is open — get ready</span>
            </>
          )}
        </div>

        {/* Presence pills */}
        <div className="lobby-presence">
          <div className={`lobby-presence-pill ${teacherJoined ? 'on' : ''}`} data-testid="presence-teacher">
            <Circle size={8} fill={teacherJoined ? '#10B981' : '#9CA3AF'} color={teacherJoined ? '#10B981' : '#9CA3AF'} />
            <span>Mentor {teacherJoined ? 'joined' : 'not yet joined'}</span>
          </div>
          <div className={`lobby-presence-pill ${studentJoined ? 'on' : ''}`} data-testid="presence-student">
            <Circle size={8} fill={studentJoined ? '#10B981' : '#9CA3AF'} color={studentJoined ? '#10B981' : '#9CA3AF'} />
            <span>Student {studentJoined ? 'joined' : 'not yet joined'}</span>
          </div>
        </div>

        {/* Join / end action */}
        <div className="lobby-action">
          {sessionDead ? (
            <button className="lobby-join-btn secondary" onClick={() => navigate('/bookings')} data-testid="lobby-back-bookings">
              Back to bookings
            </button>
          ) : (
            <button
              className="lobby-join-btn"
              onClick={joinCall}
              disabled={!canJoin() || joining}
              data-testid="lobby-join-btn"
            >
              <Video size={18} />
              {joining ? 'Joining…' : canJoin() ? 'Join call' : (
                role === 'user' && !started
                  ? `Joining automatically in ${fmtSecs(secUntilStart)}…`
                  : !lobbyOpen ? 'Lobby not open yet' : 'Not ready'
              )}
            </button>
          )}
        </div>

        <p className="lobby-fine-print">
          <Clock size={12} /> Refunds are automatic if your mentor doesn't join by the scheduled time.
        </p>
      </section>

      {/* Late-mentor modal (student only, once per booking) */}
      {showTeacherEarly && (
        <div
          data-testid="teacher-early-modal"
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 310, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
        >
          <div style={{ background: '#fff', borderRadius: 20, padding: 28, maxWidth: 440, width: '100%', color: '#1F2937', boxShadow: '0 20px 60px rgba(0,0,0,0.35)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 56, height: 56, borderRadius: '50%', background: '#DCFCE7', color: '#166534', margin: '0 auto 16px' }}>
              <Clock size={28} />
            </div>
            <h2 style={{ fontFamily: 'Fraunces, serif', fontSize: 22, fontWeight: 700, margin: '0 0 12px', textAlign: 'center' }}>
              You're a few minutes early
            </h2>
            <p style={{ margin: '0 0 12px', color: '#4B5563', lineHeight: 1.5 }}>
              Great — joining ahead of time lets you check your camera, microphone and lighting before the student arrives.
            </p>
            <ul style={{ padding: '0 0 0 20px', margin: '0 0 16px', color: '#374151', lineHeight: 1.6 }}>
              <li>If the student doesn't show, <strong>stay on the call for the full session length</strong> — you'll be paid for your time.</li>
              <li>If they join late, use whatever time remains. The lost minutes are on them.</li>
              <li>Your payment is captured the moment you're both in the room.</li>
            </ul>
            <button
              onClick={dismissTeacherEarly}
              data-testid="teacher-early-ack-btn"
              style={{ width: '100%', padding: 14, borderRadius: 999, border: 'none', background: '#2C4E46', color: '#fff', fontWeight: 700, fontSize: 15, cursor: 'pointer' }}
            >
              Got it
            </button>
          </div>
        </div>
      )}

      {showLateMentor && (
        <div
          data-testid="late-mentor-modal"
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 310, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
        >
          <div style={{ background: '#fff', borderRadius: 20, padding: 28, maxWidth: 440, width: '100%', color: '#1F2937', boxShadow: '0 20px 60px rgba(0,0,0,0.35)' }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 56, height: 56, borderRadius: '50%', background: '#FEF3C7', color: '#D97706', margin: '0 auto 16px', display: 'flex' }}>
              <Clock size={28} />
            </div>
            <h2 style={{ fontFamily: 'Fraunces, serif', fontSize: 22, fontWeight: 700, margin: '0 0 12px', textAlign: 'center' }}>
              Your mentor hasn't joined yet
            </h2>
            <p style={{ margin: '0 0 16px', color: '#4B5563', lineHeight: 1.5 }}>
              We'll give them <strong>2 more minutes</strong> to arrive. If they still haven't joined by then:
            </p>
            <ul style={{ padding: '0 0 0 20px', margin: '0 0 16px', color: '#374151', lineHeight: 1.6 }}>
              <li><strong>No money will leave your account</strong> — your card hold is released automatically</li>
              <li>You'll see a confirmation on the Bookings page</li>
              <li>You're welcome to rebook with another mentor straight away</li>
            </ul>
            <p style={{ margin: '0 0 20px', color: '#4B5563', lineHeight: 1.5 }}>
              In the meantime we'll pop you into the room so you're ready the moment they show up.
            </p>
            <button
              onClick={dismissLateMentor}
              data-testid="late-mentor-ack-btn"
              style={{
                width: '100%', padding: 14, borderRadius: 999, border: 'none',
                background: '#2C4E46', color: '#fff', fontWeight: 700, fontSize: 15,
                cursor: 'pointer',
              }}
            >
              Got it
            </button>
          </div>
        </div>
      )}

      {/* Post-call rating modal (student only) */}
      {showRating && (
        <div
          data-testid="lobby-rating-modal"
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
        >
          <div style={{ background: '#111', borderRadius: 20, padding: 28, maxWidth: 420, width: '100%', color: '#fff' }}>
            <div style={{ textAlign: 'center', marginBottom: 20 }}>
              {other.avatar_url
                ? <img src={other.avatar_url} alt="" style={{ width: 72, height: 72, borderRadius: '50%', objectFit: 'cover', margin: '0 auto 12px', display: 'block' }} />
                : <div style={{ width: 72, height: 72, borderRadius: '50%', background: '#E07A5F', margin: '0 auto 12px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                    <UserIcon size={32} />
                  </div>}
              <h2 style={{ fontFamily: 'Fraunces, serif', fontSize: 22, fontWeight: 700, margin: '0 0 4px' }}>
                How was your session with {other.full_name || 'your mentor'}?
              </h2>
              <p style={{ margin: 0, color: '#9CA3AF', fontSize: 14 }}>Your review helps other mentees pick the right mentor.</p>
            </div>

            {/* Star picker */}
            <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginBottom: 20 }} data-testid="rating-stars">
              {[1, 2, 3, 4, 5].map(n => (
                <button
                  key={n}
                  onClick={() => setRatingValue(n)}
                  data-testid={`star-${n}`}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
                  aria-label={`${n} star${n > 1 ? 's' : ''}`}
                >
                  <Star
                    size={36}
                    fill={n <= ratingValue ? '#D97706' : 'transparent'}
                    color={n <= ratingValue ? '#D97706' : '#4B5563'}
                  />
                </button>
              ))}
            </div>

            <textarea
              value={reviewText}
              onChange={(e) => setReviewText(e.target.value.slice(0, 500))}
              placeholder="Share a short review (optional)…"
              data-testid="rating-review-input"
              rows={4}
              style={{ width: '100%', padding: 12, borderRadius: 10, background: '#1A1A1A', border: '1px solid #262626', color: '#fff', fontFamily: 'inherit', fontSize: 14, resize: 'vertical', boxSizing: 'border-box' }}
            />
            <p style={{ color: '#6B7280', fontSize: 12, textAlign: 'right', margin: '4px 0 20px' }}>{reviewText.length}/500</p>

            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={skipRating}
                disabled={submittingRating}
                data-testid="rating-skip-btn"
                style={{ flex: 1, padding: 14, borderRadius: 999, border: 'none', background: '#262626', color: '#D1D5DB', fontWeight: 600, cursor: 'pointer' }}
              >Skip</button>
              <button
                onClick={submitRating}
                disabled={submittingRating}
                data-testid="rating-submit-btn"
                style={{ flex: 2, padding: 14, borderRadius: 999, border: 'none', background: '#E07A5F', color: '#fff', fontWeight: 700, cursor: submittingRating ? 'wait' : 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
              >
                <Send size={16} /> {submittingRating ? 'Submitting…' : 'Submit review'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Lobby;
